import type { StateCreator } from "zustand";
import type { RunControlState } from "./index";
import type { RunMode, RunStatus, Snapshot } from "../types";

export type PauseReason = "breakpoint" | "start-gate" | "step-mode" | "unknown" | null;

export interface RunSlice {
  runId: string | null;
  flowId: string | null;
  status: RunStatus | null;
  mode: RunMode | null;
  isBusy: boolean;
  error: string | null;
  telemetryConnected: boolean;
  snapshotConnected: boolean;
  connected: boolean;
  snapshot: Snapshot | null;
  pauseReason: PauseReason;
  currentSuspendedNodeId: string | null;
  setIds: (runId: string | null, flowId: string | null) => void;
  setMode: (mode: RunMode | null) => void;
  setStatus: (status: RunStatus | null) => void;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  setTelemetryConnected: (connected: boolean) => void;
  setSnapshotConnected: (connected: boolean) => void;
  setPause: (reason: PauseReason, nodeId: string | null) => void;
  setSnapshot: (snapshot: Snapshot | null) => void;
  clearAll: () => void;
}

export const createRunSlice: StateCreator<
  RunControlState,
  [["zustand/immer", never], ["zustand/subscribeWithSelector", never]],
  [],
  RunSlice
> = (set) => ({
  runId: null,
  flowId: null,
  status: null,
  mode: null,
  isBusy: false,
  error: null,
  telemetryConnected: false,
  snapshotConnected: false,
  connected: false,
  snapshot: null,
  pauseReason: null,
  currentSuspendedNodeId: null,

  setIds: (runId, flowId) =>
    set((state) => {
      state.runId = runId;
      state.flowId = flowId;
    }),

  setMode: (mode) =>
    set((state) => {
      state.mode = mode;
    }),

  setStatus: (status) =>
    set((state) => {
      state.status = status;
      if (status === "completed" || status === "failed" || status === "canceled") {
        state.pauseReason = null;
        state.currentSuspendedNodeId = null;
      }
    }),

  setBusy: (busy) =>
    set((state) => {
      state.isBusy = busy;
    }),

  setError: (message) =>
    set((state) => {
      state.error = message;
    }),

  setTelemetryConnected: (value) =>
    set((state) => {
      state.telemetryConnected = value;
      state.connected = value || state.snapshotConnected;
    }),

  setSnapshotConnected: (value) =>
    set((state) => {
      state.snapshotConnected = value;
      state.connected = state.telemetryConnected || value;
    }),

  setPause: (reason, nodeId) =>
    set((state) => {
      state.pauseReason = reason;
      state.currentSuspendedNodeId = nodeId;
    }),

  setSnapshot: (snapshot) =>
    set((state) => {
      state.snapshot = snapshot;
    }),

  clearAll: () =>
    set((state) => {
      state.runId = null;
      state.flowId = null;
      state.status = null;
      state.mode = null;
      state.isBusy = false;
      state.error = null;
      state.telemetryConnected = false;
      state.snapshotConnected = false;
      state.connected = false;
      state.snapshot = null;
      state.pauseReason = null;
      state.currentSuspendedNodeId = null;
    }),
});
